import SiteFooter from './features/legal/SiteFooter';
import React, { Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import api, { AUTH_SESSION_EXPIRED_EVENT } from './api/axios';
import { getErrorMessage } from './api/errorMessage';
import { ACCOUNTING_PERMISSION } from './api/accounting';
import { STORE_MARK_PAID_PERMISSION } from './api/store';
import { AuthSession, buildSteamLoginUrl, hasAdminAccess, isAuthenticatedSession } from './auth';
import RequireAdmin from './auth/RequireAdmin';
import PostLoginRedirect from './auth/PostLoginRedirect';
import RequireAuth from './auth/RequireAuth';
import './App.css';
import MatchesPage from './pages/MatchesPage';
import PlayersPage from './pages/PlayersPage';
import StatisticsPage from './pages/StatisticsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AppHeader, { AppHeaderNavGroup } from './ui/AppHeader';
import { usePresencePing } from './features/presence/usePresencePing';
import CookieConsentBanner from './ui/CookieConsentBanner/CookieConsentBanner';
import ForbiddenState from './ui/system-states/ForbiddenState';
import { EmptyState, LoadingState } from './ui/system-states';
import { ToastProvider, useAppToast } from './ui/toast';
import { CatalogProvider } from './catalog';
import { tr } from './i18n';

// Detail pages — not in the critical path; opened by click from list views
const PlayerDetailPage = React.lazy(() => import('./pages/PlayerDetailPage'));
const MatchDetailPage = React.lazy(() => import('./pages/MatchDetailPage'));
const StatisticsDetailPage = React.lazy(() => import('./pages/StatisticsDetailPage'));
const BanPresetsPage = React.lazy(() => import('./pages/BanPresetsPage'));

// Admin pages — lazily loaded; split into separate JS chunks
const AdminPermissionsPage = React.lazy(() => import('./pages/AdminPermissionsPage'));
const AnalyticsPage = React.lazy(() => import('./pages/admin/analytics/AnalyticsPage'));
const TagsPage = React.lazy(() => import('./pages/TagsPage'));
const NpcAbilitiesPage = React.lazy(() => import('./pages/NpcAbilitiesPage'));
const MatchSettingsPage = React.lazy(() => import('./pages/admin/matchSettings/MatchSettingsPage'));
const PromoCodesPage = React.lazy(() => import('./pages/admin/promoCodes/PromoCodesPage'));
const PollsPage = React.lazy(() => import('./pages/admin/polls/PollsPage'));
const DedicatedKeysPage = React.lazy(() => import('./pages/admin/dedicatedKeys/DedicatedKeysPage'));
const AdminStorePage = React.lazy(() => import('./pages/admin/store/AdminStorePage'));
const AccountingPage = React.lazy(() => import('./pages/admin/accounting/AccountingPage'));

const StorePage = React.lazy(() => import('./pages/store/StorePage'));
const StoreOrderPage = React.lazy(() => import('./pages/store/StoreOrderPage'));
const StoreTagPage = React.lazy(() => import('./pages/store/StoreTagPage'));
const StoreTermsPage = React.lazy(() => import('./pages/store/StoreLegalPages').then(m => ({ default: m.StoreTermsPage })));

const navGroups: AppHeaderNavGroup[] = [
  {
    id: 'community',
    label: '',
    adminOnly: false,
    items: [
      { to: '/matches', label: tr.shell.nav.matches },
      { to: '/statistics', label: tr.shell.nav.statistics },
      { to: '/players', label: tr.shell.nav.players },
      { to: '/store', label: tr.shell.nav.store, highlight: true },
      { to: '/presets', label: tr.shell.nav.banPresets, requiresAuth: true, requiresPermission: 'CABINET_BAN_PRESETS_EDIT' }
    ]
  },
  {
    id: 'admin',
    label: tr.shell.nav.admin,
    adminOnly: true,
    items: [
      { to: '/admin/analytics', label: tr.shell.nav.analytics },
      { to: '/admin/permissions', label: tr.shell.nav.permissions },
      { to: '/admin/tags', label: tr.shell.nav.tags },
      { to: '/admin/configuration', label: tr.shell.nav.catalogs },
      { to: '/admin/match-settings', label: tr.shell.nav.matchSettings },
      { to: '/admin/promo-codes', label: tr.shell.nav.promoCodes },
      { to: '/admin/polls', label: tr.shell.nav.polls },
      { to: '/admin/dedicated-keys', label: tr.shell.nav.dedicatedKeys },
      { to: '/admin/store', label: tr.store.navTitle },
      { to: '/admin/accounting', label: tr.shell.nav.accounting, requiresPermission: ACCOUNTING_PERMISSION }
    ]
  }
];

const STEAM_ERROR_MESSAGES: Record<string, string> = {
  steam_validation_failed: tr.shell.app.steamValidationFailed,
  access_denied: tr.shell.app.accessDenied,
  account_disabled: tr.shell.app.accountDisabled
};

const getPageTitle = (pathname: string) => {
  if (pathname === '/') return tr.common.sidebar.profileTitle;
  if (pathname.startsWith('/privacy')) return tr.common.sidebar.privacyPolicy;
  if (pathname.startsWith('/statistics')) return tr.shell.nav.statistics;
  if (pathname.startsWith('/players/')) return tr.shell.pageTitle.player;
  if (pathname === '/players') return tr.shell.nav.players;
  if (pathname.startsWith('/matches/')) return tr.shell.pageTitle.match;
  if (pathname === '/matches') return tr.shell.nav.matches;
  if (pathname.startsWith('/admin/analytics')) return tr.shell.nav.analytics;
  if (pathname.startsWith('/admin/tags')) return tr.shell.nav.tags;
  if (pathname.startsWith('/admin/permissions')) return tr.shell.nav.permissions;
  if (pathname.startsWith('/admin/configuration')) return tr.shell.nav.catalogs;
  if (pathname.startsWith('/admin/match-settings')) return tr.shell.nav.matchSettings;
  if (pathname.startsWith('/admin/promo-codes')) return tr.shell.nav.promoCodes;
  if (pathname.startsWith('/admin/polls')) return tr.shell.nav.polls;
  if (pathname.startsWith('/admin/dedicated-keys')) return tr.shell.nav.dedicatedKeys;
  if (pathname.startsWith('/admin/store')) return tr.store.navTitle;
  if (pathname.startsWith('/admin/accounting')) return tr.shell.nav.accounting;
  if (pathname === '/terms') return tr.store.pageTitle.terms;
  if (pathname.startsWith('/store/orders/')) return tr.store.pageTitle.order;
  if (pathname === '/store/tag') return tr.storeTag.page.kicker;
  if (pathname.startsWith('/store')) return tr.store.navTitle;
  if (pathname.startsWith('/presets')) return tr.shell.nav.banPresets;
  return 'Ratten Run';
};


// Старый редактор жил на /presets/:index/edit; теперь выбор пресета приходит параметром ?preset
const LegacyPresetEditRedirect: React.FC = () => {
  const { index = '' } = useParams<{ index: string }>();
  return <Navigate to={`/presets?preset=${encodeURIComponent(index)}`} replace />;
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [authLoading, setAuthLoading] = useState(true);
  const [session, setSession] = useState<AuthSession | null>(null);
  const { pushToast } = useAppToast();

  useEffect(() => {
    let active = true;

    const loadSession = async () => {
      try {
        const response = await api.get<AuthSession>('/api/auth/me');
        if (!active) return;

        if (response.data?.authenticated) {
          setSession({
            authenticated: true,
            steamId: response.data.steamId,
            playerUid: response.data.playerUid,
            displayName: response.data.displayName,
            avatarUrl: response.data.avatarUrl,
            roles: response.data.roles || [],
            permissions: response.data.permissions || []
          });
        } else {
          setSession(null);
        }
      } catch (error: any) {
        if (!active) return;
        if (error?.response?.status !== 401) {
          pushToast(getErrorMessage(error, tr.shell.app.sessionFetchFailed));
        }
        setSession(null);
      } finally {
        if (active) {
          setAuthLoading(false);
        }
      }
    };

    loadSession();

    return () => {
      active = false;
    };
  }, [pushToast]);

  // Глобальный сигнал от axios interceptor: любой 401 (кроме /auth/me) трактуется
  // как истёкшая сессия. Сбрасываем локальное состояние, дальше гейты RequireAuth
  // / RequireAdmin сами уведут пользователя с приватной страницы через React Router.
  useEffect(() => {
    const onSessionExpired = () => setSession(null);
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  const authenticatedSession = isAuthenticatedSession(session);
  const adminAccess = hasAdminAccess(session);
  const strictAdminAccess = session?.roles.includes('ADMIN') ?? false;
  usePresencePing(!authLoading, session?.playerUid ?? '');
  const sessionPermissions = session?.permissions || [];
  const canEditBanPresets = sessionPermissions.includes('CABINET_BAN_PRESETS_EDIT');
  const canMarkStoreOrderPaid = sessionPermissions.includes(STORE_MARK_PAID_PERMISSION);
  const canViewAccounting = strictAdminAccess && sessionPermissions.includes(ACCOUNTING_PERMISSION);

  // Sync document title with current route so the browser tab reflects the page.
  useEffect(() => {
    document.title = `${getPageTitle(location.pathname)} · Ratten Run`;
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      setSession(null);
      navigate('/', { replace: true });
    }
  };

  const handleLogin = () => {
    window.location.href = buildSteamLoginUrl();
  };

  // Surface Steam-callback errors (?error=...) via toast and clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('error');
    if (!code) return;
    const msg = STEAM_ERROR_MESSAGES[code] || tr.shell.app.authError(code);
    pushToast(msg);
    params.delete('error');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [location.search, location.pathname, navigate, pushToast]);

  if (authLoading) {
    return (
      <div className="app-shell">
        <div className="app-grid-overlay" />
        <main className="app-content">
          <div className="app-page-frame">
            <div className="section-panel page-stack">
              <div className="panel-kicker">{tr.shell.app.sessionKicker}</div>
              <h2 className="mb-0">{tr.shell.app.checkingAccess}</h2>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const routes = (
    <Suspense fallback={<LoadingState />}>
    <Routes>
      {/* Root: the personal cabinet now lives on the player's own detail page.
          Authenticated with a game profile → their /players/:uid; authenticated
          without one → a hint; anonymous → public statistics. The bare `/` URL is
          freed up for a future landing page. */}
      <Route
        path="/"
        element={
          !authenticatedSession
            ? <Navigate to="/statistics" replace />
            : (
              <PostLoginRedirect>
                {session?.playerUid
                  ? <Navigate to={`/players/${encodeURIComponent(session.playerUid)}`} replace />
                  : (
                    <EmptyState
                      title={tr.shell.app.noPlayerProfileTitle}
                      description={tr.shell.app.noPlayerProfileDescription}
                    />
                  )}
              </PostLoginRedirect>
            )
        }
      />

      {/* Legacy /login URL: just bounce to home; errors are surfaced via toast in AppContent. */}
      <Route path="/login" element={<Navigate to="/" replace />} />

      {/* Public routes — no auth required */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/statistics" element={<StatisticsPage />} />
      <Route path="/statistics/heroes/:name" element={<StatisticsDetailPage kind="hero" />} />
      <Route path="/statistics/abilities/:name" element={<StatisticsDetailPage kind="ability" />} />
      <Route path="/statistics/items/:name" element={<StatisticsDetailPage kind="item" />} />
      <Route path="/players" element={<PlayersPage selfUid={session?.playerUid ?? null} viewAsAdmin={adminAccess} />} />
      <Route path="/players/:uid" element={<PlayerDetailPage viewAsAdmin={adminAccess} canManageTags={strictAdminAccess} selfUid={session?.playerUid ?? null} />} />
      <Route path="/matches" element={<MatchesPage selfUid={session?.playerUid ?? null} />} />
      <Route path="/matches/:matchId" element={<MatchDetailPage canSendCommands={strictAdminAccess} chatAccess={adminAccess} selfUid={session?.playerUid ?? null} />} />

      {/* Магазин: публичный, без ссылок из навигации; /store/orders/:id открывается по токену */}
      <Route path="/store" element={<StorePage session={session} />} />
      <Route
        path="/store/tag"
        element={
          <RequireAuth
            authenticated={authenticatedSession}
            title={tr.storeTag.page.loginTitle}
            description={tr.storeTag.page.loginDescription}
            returnTo="/store/tag"
          >
            <StoreTagPage session={session} />
          </RequireAuth>
        }
      />
      <Route path="/store/orders/:orderId" element={<StoreOrderPage />} />
      <Route path="/terms" element={<StoreTermsPage />} />
      <Route path="/store/terms" element={<Navigate to="/terms" replace />} />
      <Route path="/store/privacy" element={<Navigate to="/privacy" replace />} />

      {/* Authenticated-only — personal */}
      <Route
        path="/presets"
        element={
          <RequireAuth authenticated={authenticatedSession}>
            {canEditBanPresets ? (
              <BanPresetsPage />
            ) : (
              <ForbiddenState
                title={tr.shell.app.presetsForbiddenTitle}
                description={tr.shell.app.presetsForbiddenDescription}
                actions={[{ label: tr.common.actions.toHome, href: '/', variant: 'outline' }]}
              />
            )}
          </RequireAuth>
        }
      />
      <Route path="/presets/:index/edit" element={<LegacyPresetEditRedirect />} />

      {/* Admin-only */}
      <Route
        path="/admin/analytics"
        element={<RequireAdmin authenticated={authenticatedSession} admin={strictAdminAccess}><AnalyticsPage /></RequireAdmin>}
      />
      <Route
        path="/admin/tags"
        element={<RequireAdmin authenticated={authenticatedSession} admin={strictAdminAccess}><TagsPage /></RequireAdmin>}
      />
      <Route
        path="/admin/permissions"
        element={<RequireAdmin authenticated={authenticatedSession} admin={strictAdminAccess}><AdminPermissionsPage selfSteamId={session?.steamId ?? null} /></RequireAdmin>}
      />
      <Route
        path="/admin/configuration"
        element={<RequireAdmin authenticated={authenticatedSession} admin={strictAdminAccess}><NpcAbilitiesPage /></RequireAdmin>}
      />
      <Route
        path="/admin/match-settings"
        element={<RequireAdmin authenticated={authenticatedSession} admin={strictAdminAccess}><MatchSettingsPage /></RequireAdmin>}
      />
      <Route
        path="/admin/promo-codes"
        element={<RequireAdmin authenticated={authenticatedSession} admin={strictAdminAccess}><PromoCodesPage /></RequireAdmin>}
      />
      <Route
        path="/admin/polls"
        element={<RequireAdmin authenticated={authenticatedSession} admin={strictAdminAccess}><PollsPage /></RequireAdmin>}
      />
      <Route
        path="/admin/dedicated-keys"
        element={<RequireAdmin authenticated={authenticatedSession} admin={strictAdminAccess}><DedicatedKeysPage /></RequireAdmin>}
      />

      <Route
        path="/admin/store"
        element={<RequireAdmin authenticated={authenticatedSession} admin={strictAdminAccess}><AdminStorePage canMarkPaid={canMarkStoreOrderPaid} /></RequireAdmin>}
      />

      <Route
        path="/admin/accounting"
        element={<RequireAdmin authenticated={authenticatedSession} admin={canViewAccounting}><AccountingPage /></RequireAdmin>}
      />

      {/* Legacy redirects — keep working */}
      <Route path="/cabinet" element={<Navigate to="/" replace />} />
      <Route path="/me/matches" element={<Navigate to="/matches?my=1" replace />} />
      <Route path="/tags" element={<Navigate to="/admin/tags" replace />} />
      <Route path="/permissions" element={<Navigate to="/admin/permissions" replace />} />
      <Route path="/npc-abilities" element={<Navigate to="/admin/configuration" replace />} />
    </Routes>
    </Suspense>
  );

  // Always render the cabinet layout. Header adapts to auth state; routes filter behind guards.
  const visibleNavGroups = navGroups
    .filter(group => !group.adminOnly || strictAdminAccess)
    .map(group => ({
      ...group,
      items: group.items.filter(item => {
        if (item.requiresAuth && !authenticatedSession) return false;
        if (item.requiresPermission && !sessionPermissions.includes(item.requiresPermission)) return false;
        return true;
      })
    }));

  return (
    <div className="app-shell app-shell-cabinet">
      <div className="app-grid-overlay" />

      <div className="cabinet-layout">
        <AppHeader
          navGroups={visibleNavGroups}
          onLogout={handleLogout}
          onLoginClick={handleLogin}
          session={session}
          showOnlineCounter={strictAdminAccess}
        />

        <main className="app-content cabinet-main-content">
          <div className="app-page-frame">
            {routes}
          </div>
        </main>

        <SiteFooter />
      </div>

      <CookieConsentBanner />
    </div>
  );
};

function App() {
  return (
    <ToastProvider>
      <CatalogProvider>
        <AppContent />
      </CatalogProvider>
    </ToastProvider>
  );
}

export default App;
